import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Box,
  IconButton,
  useTheme
} from '@mui/material';
import { Close, History, Timeline, Send, Devices, Block, Person, CardMembership, StickyNote2 } from '@mui/icons-material';
import { User } from '../../resources/types/usersTypes';
import UsersDialogContent from '../../pages/UsersPage/UsersDialogContent';
import UserHistoryContent from '../../containers/UserHistory/UserHistoryContent';
import ContractsHistoryContent from '../ContractsHistoryModal/ContractsHistoryContent';
import UserPushContent from '../UserPushModal/UserPushContent';
import UserSessionsContent from '../UserSessionsModal/UserSessionsContent';
import UserBanContent from '../UserBanModal/UserBanContent';
import UserSubscriptionsContent from '../UserSubscriptionsModal/UserSubscriptionsContent';
import UserNotesContent from '../UserNotesModal/UserNotesContent';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-settings-tabpanel-${index}`}
      aria-labelledby={`user-settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
  user: User | null;
  isSuperAdmin?: boolean;
}

const UserSettingsModal = ({ open, onClose, user, isSuperAdmin = false }: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const tabs = [
    { label: t('userModal.tabUser'), icon: <Person fontSize="small" /> },
    { label: t('userModal.tabTransactionHistory'), icon: <History fontSize="small" /> },
    { label: t('userModal.tabTripHistory'), icon: <Timeline fontSize="small" /> },
    { label: t('userModal.tabPush'), icon: <Send fontSize="small" /> },
    { label: t('userModal.tabSubscriptions'), icon: <CardMembership fontSize="small" /> },
    { label: t('userModal.tabNotes'), icon: <StickyNote2 fontSize="small" /> },
    ...(isSuperAdmin ? [
      { label: t('userModal.tabActiveSessions'), icon: <Devices fontSize="small" /> },
      { label: t('userModal.tabBan'), icon: <Block fontSize="small" /> }
    ] : [])
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: '500px'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`
      }}>
        {t('userModal.title')}: {user?.firstName} {user?.lastName}
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          borderBottom: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`,
          px: 2,
          '& .MuiTab-root': {
            minHeight: 56,
            minWidth: 'auto',
            textTransform: 'none',
            fontSize: 14,
            fontWeight: 500,
            whiteSpace: 'nowrap'
          }
        }}
      >
        {tabs.map((tab, index) => (
          <Tab 
            key={index}
            label={tab.label} 
            icon={tab.icon}
            iconPosition="start"
          />
        ))}
      </Tabs>

      <DialogContent sx={{ p: 0, overflow: 'auto', maxHeight: '60vh' }}>
        <TabPanel value={activeTab} index={0}>
          {user?._id && <UsersDialogContent userId={user._id} />}
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {user?._id && <UserHistoryContent userId={user._id} />}
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          {user?._id && <ContractsHistoryContent userId={user._id} />}
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          {user && <UserPushContent user={user} />}
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          {user?._id && <UserSubscriptionsContent userId={user._id} />}
        </TabPanel>

        <TabPanel value={activeTab} index={5}>
          {user?._id && <UserNotesContent userId={user._id} />}
        </TabPanel>

        {isSuperAdmin && (
          <>
            <TabPanel value={activeTab} index={6}>
              {user && <UserSessionsContent user={user} />}
            </TabPanel>

            <TabPanel value={activeTab} index={7}>
              {user && <UserBanContent user={user} />}
            </TabPanel>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UserSettingsModal;
